import { useCallback, useMemo } from 'react';
import { translations, type Locale, type TranslationKey } from '../i18n/translations';

/**
 * Lightweight translation hook for the widget.
 * Returns a `t()` function that resolves keys with optional {placeholder} interpolation.
 * Falls back to English if the locale is unknown or a key is missing.
 */
export function useTranslation(locale: string | null | undefined) {
  const resolvedLocale: Locale = useMemo(() => {
    if (!locale) return 'en';
    const lower = locale.toLowerCase();
    if (lower === 'vi' || lower.startsWith('vi-')) return 'vi';
    return 'en';
  }, [locale]);

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string | number>): string => {
      const dict = translations[resolvedLocale] || translations.en;
      let text = dict[key] ?? translations.en[key] ?? key;

      if (params) {
        for (const [k, v] of Object.entries(params)) {
          text = text.split(`{${k}}`).join(String(v));
        }
      }

      return text;
    },
    [resolvedLocale]
  );

  return { t, locale: resolvedLocale };
}
